import React from 'react';

import type {
  QuoteCatalogPartItem,
  QuoteLineItemDraft,
  QuoteTemplateLayout,
} from './TSFADAnalysis.shared';
import { createQuoteLineId } from './TSFADAnalysis.shared';
import {
  getIncludedQuoteLineColumns,
  getQuoteLineColumnDisplayValue,
  type QuoteLineColumnConfig
} from '../utils/quoteLineColumns';
import {
  buildQuoteTotalDisplayRowsFromSummary,
  formatQuoteTotalRowValue,
  type QuoteTotalsSummary
} from '../utils/quoteTotals';

type StylesMap = Record<string, any>;

export type TSFADQuoteHeaderBlock = {
  id: string;
  slotId?: string;
  label: string;
  slotRow?: number;
  slotCol?: number;
  textAlign?: "left" | "center" | "right";
  fields: Array<{
    id: string;
    label: string;
    value: string;
  }>;
};

type TSFADQuoteModalProps = {
  open: boolean;
  styles: StylesMap;
  quoteHasActiveSla: boolean;
  quoteTemplateName: string | null;
  quoteEditingQuoteId: string | null;
  quoteRevisionOfQuoteId: string | null;
  quoteHeaderBlocks: TSFADQuoteHeaderBlock[];
  quoteTitle: string;
  setQuoteTitle: React.Dispatch<React.SetStateAction<string>>;
  quoteDescription: string;
  setQuoteDescription: React.Dispatch<React.SetStateAction<string>>;
  quoteLineItems: QuoteLineItemDraft[];
  setQuoteLineItems: React.Dispatch<React.SetStateAction<QuoteLineItemDraft[]>>;
  selectedQuoteLineIds: string[];
  setSelectedQuoteLineIds: React.Dispatch<React.SetStateAction<string[]>>;
  applyPartNumberToQuoteLine: (index: number, value: string) => void;
  quotePartCatalog: QuoteCatalogPartItem[];
  quoteTotals: QuoteTotalsSummary;
  quoteUnmatchedParts: string[];
  quoteNotes: string;
  setQuoteNotes: React.Dispatch<React.SetStateAction<string>>;
  quoteTemplateLayout: QuoteTemplateLayout | null;
  quoteCustomerName: string;
  setQuoteCustomerName: React.Dispatch<React.SetStateAction<string>>;
  quoteCustomerSignature: string;
  setQuoteCustomerSignature: React.Dispatch<React.SetStateAction<string>>;
  quoteSaving: boolean;
  quoteSaveButtonLabel: string;
  onSave: () => void | Promise<void>;
  onClose: () => void;
};

export const TSFADQuoteModal: React.FC<TSFADQuoteModalProps> = ({
  open,
  styles,
  quoteTemplateName,
  quoteEditingQuoteId,
  quoteRevisionOfQuoteId,
  quoteHeaderBlocks,
  quoteTitle,
  setQuoteTitle,
  quoteDescription,
  setQuoteDescription,
  quoteLineItems,
  setQuoteLineItems,
  selectedQuoteLineIds,
  setSelectedQuoteLineIds,
  applyPartNumberToQuoteLine,
  quotePartCatalog,
  quoteTotals,
  quoteUnmatchedParts,
  quoteNotes,
  setQuoteNotes,
  quoteTemplateLayout,
  quoteCustomerName,
  setQuoteCustomerName,
  quoteCustomerSignature,
  setQuoteCustomerSignature,
  quoteSaving,
  quoteSaveButtonLabel,
  onSave,
  onClose,
}) => {
  if (!open) return null;
  const quoteTotalRows = buildQuoteTotalDisplayRowsFromSummary(
    quoteTotals,
    quoteTemplateLayout?.totalSettings
  );
  const approvalPlacementRaw = String(quoteTemplateLayout?.approvalPlacement || '').trim();
  const approvalPlacement =
    approvalPlacementRaw === 'footer_c1' ||
    approvalPlacementRaw === 'footer_c2' ||
    approvalPlacementRaw === 'footer_c3'
      ? approvalPlacementRaw
      : approvalPlacementRaw === 'footer'
        ? 'footer_c1'
      : 'footer_c3';
  const footerSlotOrder = ['footer_c1', 'footer_c2', 'footer_c3'] as const;
  const approvalFooterSlots = (() => {
    const slots: Array<'footer_c1' | 'footer_c2' | 'footer_c3'> = [];
    const addSlot = (value: any) => {
      const raw = String(value || '').trim();
      if ((raw === 'footer_c1' || raw === 'footer_c2' || raw === 'footer_c3') && !slots.includes(raw)) {
        slots.push(raw);
      }
    };
    const layoutApprovalFooterSlots = quoteTemplateLayout?.approvalFooterSlots;
    if (Array.isArray(layoutApprovalFooterSlots)) {
      layoutApprovalFooterSlots.forEach(addSlot);
    }
    if (slots.length === 0) addSlot(approvalPlacement);
    return slots.sort((a, b) => footerSlotOrder.indexOf(a) - footerSlotOrder.indexOf(b));
  })();
  const renderFooterSignatureEditor = () => {
    if (approvalFooterSlots.length === 0) return null;
    const selectedColumns = approvalFooterSlots.map(slot => footerSlotOrder.indexOf(slot) + 1);
    const startColumn = Math.min(...selectedColumns);
    const endColumn = Math.max(...selectedColumns);
    const span = endColumn - startColumn + 1;
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '0.75rem' }}>
        {[1, 2, 3].map(column =>
          column < startColumn || column > endColumn ? (
            <div
              key={`quote-footer-signature-editor-${column}`}
              style={{ border: '1px solid #d1d5db', borderRadius: '0.5rem', minHeight: '120px' }}
            />
          ) : column === startColumn ? (
            <div
              key={`quote-footer-signature-editor-${column}`}
              style={{ border: '1px solid #d1d5db', borderRadius: '0.5rem', padding: '0.65rem 0.75rem', gridColumn: `span ${span}` }}
            >
              {renderSignatureEditor()}
            </div>
          ) : null
        )}
      </div>
    );
  };
  const renderSignatureEditor = () => {
    if (!quoteTemplateLayout?.requireSignature && !quoteTemplateLayout?.requireTechnicianSignature) return null;
    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: '0.75rem',
          marginBottom: '0.75rem'
        }}
      >
        {quoteTemplateLayout?.requireSignature && (
          <div>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.35rem' }}>
              {quoteTemplateLayout.signatureLabel || 'Customer approval signature'}
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '0.5rem' }}>
              <input
                style={styles.input}
                value={quoteCustomerName}
                onChange={(e) => setQuoteCustomerName(e.target.value)}
                placeholder="Customer name"
              />
              <input
                style={styles.input}
                value={quoteCustomerSignature}
                onChange={(e) => setQuoteCustomerSignature(e.target.value)}
                placeholder="Signature (text / captured reference)"
              />
            </div>
          </div>
        )}
        {quoteTemplateLayout?.requireTechnicianSignature && (
          <div style={{ border: '1px solid #d1d5db', borderRadius: '0.5rem', padding: '0.65rem 0.75rem', minHeight: '96px' }}>
            <div style={{ fontWeight: 600, marginBottom: '0.35rem' }}>
              {quoteTemplateLayout.technicianSignatureLabel || 'Technician signature'}
            </div>
            <div style={{ fontSize: '0.9rem', lineHeight: 1.45, display: 'grid', gap: '0.65rem' }}>
              <div>Name: __________________</div>
              <div style={{ paddingTop: '1.3rem' }}>Signature: __________________</div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const quoteLineColumns = getIncludedQuoteLineColumns(quoteTemplateLayout?.lineColumns);
  const quoteLineColumnWidth = (column: QuoteLineColumnConfig): string => {
    switch (column.id) {
      case 'lineType':
        return '100px';
      case 'partNumber':
        return '180px';
      case 'description':
        return 'minmax(240px, 1fr)';
      case 'quantity':
        return '90px';
      case 'unitValue':
        return '110px';
      case 'discount':
        return '100px';
      case 'free':
        return '120px';
      case 'lineTotal':
        return '120px';
      default:
        return 'minmax(100px, auto)';
    }
  };
  const lineItemsGridTemplate = ['32px', ...quoteLineColumns.map(quoteLineColumnWidth)].join(' ');
  const renderStaticQuoteLineCell = (
    line: QuoteLineItemDraft,
    column: QuoteLineColumnConfig,
    lineTotal: number
  ) => (
    <div
      style={{
        color: '#1f2937',
        fontWeight: column.id === 'lineTotal' ? 700 : 600,
        textAlign: column.align,
        width: '100%'
      }}
    >
      {getQuoteLineColumnDisplayValue({ ...line, lineTotal }, column.id)}
    </div>
  );
  const renderQuoteLineColumnEditor = (
    line: QuoteLineItemDraft,
    index: number,
    column: QuoteLineColumnConfig
  ) => {
    const quantity = Number.isFinite(Number(line.quantity)) ? Math.max(0, Number(line.quantity)) : 0;
    const unitValue = Number.isFinite(Number(line.unitValue)) ? Math.max(0, Number(line.unitValue)) : 0;
    const lineTotal = line.isFree ? 0 : quantity * unitValue;
    const isPartLine = String(line.lineType || '').toLowerCase() === 'part';
    const originalUnitValue = Number.isFinite(Number(line.originalUnitValue))
      ? Math.max(0, Number(line.originalUnitValue))
      : null;
    const showOriginalUnitValue =
      isPartLine &&
      line.slaCoverageStatus === 'DISCOUNTED' &&
      originalUnitValue !== null &&
      originalUnitValue > unitValue + 0.0001;

    switch (column.id) {
      case 'lineType':
        return (
          <select
            style={styles.select}
            value={line.lineType}
            onChange={(e) =>
              setQuoteLineItems(prev =>
                prev.map((item, itemIndex) => {
                  if (itemIndex !== index) return item;
                  const nextType: QuoteLineItemDraft['lineType'] =
                    e.target.value === 'labor' ? 'labor' : 'part';
                  const nextItem: QuoteLineItemDraft = {
                    ...item,
                    lineType: nextType,
                    isFree: false,
                    originalUnitValue: null,
                    slaCoverageStatus: null,
                    slaLabel: null,
                    slaDiscountPercent: null,
                    slaCoverageReason: null,
                    slaMatchedListNames: []
                  };
                  if (nextType === 'labor') {
                    return {
                      ...nextItem,
                      partNumber: null,
                      sku: null,
                      catalogId: null,
                      source: 'manual'
                    };
                  }
                  return {
                    ...nextItem,
                    source: 'manual'
                  };
                })
              )
            }
          >
            <option value="part">Part</option>
            <option value="labor">Labor</option>
          </select>
        );
      case 'partNumber':
        return (
          <input
            style={styles.input}
            type="text"
            list={isPartLine ? 'tsfad-quote-part-options' : undefined}
            value={isPartLine ? String(line.partNumber || '') : ''}
            disabled={!isPartLine}
            onChange={(e) => applyPartNumberToQuoteLine(index, e.target.value)}
            placeholder={isPartLine ? 'Part number / SKU' : 'N/A'}
          />
        );
      case 'description':
        return (
          <input
            style={styles.input}
            type="text"
            value={line.description}
            onChange={(e) =>
              setQuoteLineItems(prev =>
                prev.map((item, itemIndex) =>
                  itemIndex === index
                    ? {
                        ...item,
                        description: e.target.value,
                        source:
                          String(item.lineType || '').toLowerCase() === 'part'
                            ? 'manual'
                            : item.source
                      }
                    : item
                )
              )
            }
            placeholder="Description"
          />
        );
      case 'quantity':
        return (
          <input
            style={{ ...styles.input, textAlign: 'center' }}
            type="number"
            min={0}
            step={0.1}
            value={quantity}
            onChange={(e) =>
              setQuoteLineItems(prev =>
                prev.map((item, itemIndex) =>
                  itemIndex === index
                    ? { ...item, quantity: Number(e.target.value || 0) }
                    : item
                )
              )
            }
          />
        );
      case 'unitValue':
        return (
          <div style={{ display: 'grid', gap: '0.2rem' }}>
            <input
              style={{ ...styles.input, textAlign: 'center' }}
              type="number"
              min={0}
              step={0.01}
              value={unitValue}
              onChange={(e) =>
                setQuoteLineItems(prev =>
                  prev.map((item, itemIndex) =>
                    itemIndex === index
                      ? {
                          ...item,
                          unitValue: Number(e.target.value || 0),
                          isFree: false,
                          originalUnitValue: null,
                          slaCoverageStatus: null,
                          slaLabel: null,
                          slaDiscountPercent: null,
                          slaCoverageReason: null,
                          slaMatchedListNames: [],
                          source:
                            String(item.lineType || '').toLowerCase() === 'part'
                              ? 'manual'
                              : item.source
                        }
                      : item
                  )
                )
              }
            />
            {showOriginalUnitValue ? (
              <div style={{ fontSize: '0.72rem', color: '#64748b', textAlign: 'center' }}>
                Original ${originalUnitValue!.toFixed(2)}
              </div>
            ) : null}
          </div>
        );
      case 'discount':
      case 'free':
      case 'lineTotal':
      default:
        return renderStaticQuoteLineCell(line, column, lineTotal);
    }
  };

  return (
    <div style={styles.modal} onClick={onClose}>
      <div style={{ ...styles.modalContent, maxWidth: '1220px' }} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h3 style={styles.modalTitle}>Quotation</h3>
          <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>
            Template: {quoteTemplateName || 'Not set'}
          </div>
        </div>

        {!quoteEditingQuoteId && quoteRevisionOfQuoteId && (
          <div style={{ ...styles.inlineNotice, marginBottom: '0.75rem' }}>
            Adjustment revision mode: saving this quote creates a new draft from quote {quoteRevisionOfQuoteId}.
          </div>
        )}

                <div style={styles.modalBody}>
          {quoteHeaderBlocks.length > 0 && (
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ fontWeight: 600, marginBottom: '0.4rem' }}>Template Header Details</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '0.5rem' }}>
                {quoteHeaderBlocks.map(block => (
                  <div
                    key={`quote-header-${block.slotId || block.id}`}
                    style={{
                      border: '1px solid #d1d5db',
                      borderRadius: '0.5rem',
                      padding: '0.55rem 0.65rem',
                      gridRow: block.slotRow ? `${block.slotRow}` : undefined,
                      gridColumn: block.slotCol ? `${block.slotCol}` : undefined,
                      textAlign: block.textAlign || 'left'
                    }}
                  >
                    <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{block.label}</div>
                    {block.fields.map(field => (
                      <div
                        key={`quote-header-field-${field.id}`}
                        style={{ fontSize: '0.82rem', lineHeight: 1.4, textAlign: block.textAlign || 'left' }}
                      >
                        <span style={{ color: '#6b7280' }}>{field.label}: </span>
                        <span>{field.value}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{ marginBottom: '0.75rem' }}>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.35rem' }}>Quote Title</label>
            <input
              style={styles.input}
              value={quoteTitle}
              onChange={(e) => setQuoteTitle(e.target.value)}
              placeholder="TSFAD Proforma Quote"
            />
          </div>

          <div style={{ marginBottom: '0.75rem' }}>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.35rem' }}>Description</label>
            <textarea
              style={styles.textarea}
              value={quoteDescription}
              onChange={(e) => setQuoteDescription(e.target.value)}
              rows={3}
            />
          </div>

          <div style={{ marginBottom: '0.75rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.35rem' }}>
              <label style={{ fontWeight: 600 }}>Line Items</label>
              <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                <button
                  style={{ ...styles.button, ...styles.secondaryButton, minWidth: '120px', padding: '0.4rem 0.65rem' }}
                  onClick={() =>
                    setQuoteLineItems(prev => [
                      ...prev,
                      {
                        id: createQuoteLineId('line', prev.length),
                        lineType: 'part',
                        description: '',
                        quantity: 1,
                        unitValue: 0,
                        isFree: false,
                        source: 'manual',
                        catalogId: null,
                        sku: null,
                        partNumber: null,
                        originalUnitValue: null,
                        slaCoverageStatus: null,
                        slaLabel: null,
                        slaDiscountPercent: null,
                        slaCoverageReason: null,
                        slaMatchedListNames: []
                      }
                    ])
                  }
                >
                  Add Line
                </button>
                <button
                  style={{ ...styles.button, ...styles.secondaryButton, minWidth: '140px', padding: '0.4rem 0.65rem' }}
                  onClick={() =>
                    setSelectedQuoteLineIds(
                      selectedQuoteLineIds.length === quoteLineItems.length
                        ? []
                        : quoteLineItems.map(item => item.id)
                    )
                  }
                  disabled={quoteLineItems.length === 0}
                >
                  {selectedQuoteLineIds.length === quoteLineItems.length ? 'Clear Select' : 'Select All'}
                </button>
                <button
                  style={{ ...styles.button, ...styles.secondaryButton, minWidth: '160px', padding: '0.4rem 0.65rem' }}
                  onClick={() => {
                    if (selectedQuoteLineIds.length === 0) return;
                    setQuoteLineItems(prev => prev.filter(item => !selectedQuoteLineIds.includes(item.id)));
                    setSelectedQuoteLineIds([]);
                  }}
                  disabled={selectedQuoteLineIds.length === 0}
                >
                  Remove Selected
                </button>
              </div>
            </div>
            <div style={{ border: '1px solid #e5e7eb', borderRadius: '0.375rem', overflow: 'hidden' }}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: lineItemsGridTemplate,
                  gap: '0.4rem',
                  padding: '0.45rem',
                  background: '#f8fafc',
                  borderBottom: '1px solid #e5e7eb',
                  alignItems: 'center',
                  fontSize: '0.8rem',
                  fontWeight: 700,
                  color: '#374151'
                }}
              >
                <div />
                {quoteLineColumns.map(column => (
                  <div key={`quote-line-head-${column.id}`} style={{ textAlign: column.align }}>
                    {column.label}
                  </div>
                ))}
              </div>
              {quoteLineItems.map((line, index) => (
                <div
                  key={line.id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: lineItemsGridTemplate,
                    gap: '0.4rem',
                    padding: '0.45rem',
                    borderBottom: '1px solid #f3f4f6',
                    alignItems: 'center'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedQuoteLineIds.includes(line.id)}
                    onChange={(e) =>
                      setSelectedQuoteLineIds(prev =>
                        e.target.checked
                          ? [...prev, line.id]
                          : prev.filter(itemId => itemId !== line.id)
                      )
                    }
                  />
                  {quoteLineColumns.map(column => (
                    <div key={`quote-line-${line.id}-${column.id}`}>
                      {renderQuoteLineColumnEditor(line, index, column)}
                    </div>
                  ))}
                </div>
              ))}
            </div>
            <datalist id="tsfad-quote-part-options">
              {quotePartCatalog.map(item => {
                const value = String(item.partNumber || item.sku || '').trim();
                if (!value) return null;
                return (
                  <option key={`quote-part-option-${item.id}`} value={value}>
                    {`${item.description} | $${Number(item.unitValue || 0).toFixed(2)}`}
                  </option>
                );
              })}
            </datalist>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.6rem' }}>
              <div
                style={{
                  minWidth: '260px',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.5rem',
                  padding: '0.55rem 0.75rem',
                  background: '#f9fafb',
                  display: 'grid',
                  gap: '0.25rem'
                }}
              >
                {(quoteTotalRows.length > 0
                  ? quoteTotalRows
                  : [{ id: 'grandTotal', label: 'Total', value: quoteTotals.grandTotal, emphasised: true }]
                ).map(row => (
                  <div
                    key={`quote-total-${row.id}`}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontSize: row.emphasised ? '0.95rem' : '0.85rem',
                      ...(row.emphasised ? { borderTop: '1px solid #d1d5db', paddingTop: '0.3rem', fontWeight: 700 } : {})
                    }}
                  >
                    <span>{row.label}</span>
                    <strong>{formatQuoteTotalRowValue(row)}</strong>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {quoteUnmatchedParts.length > 0 && (
            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.35rem' }}>
                Parts not found in price list
              </label>
              <div style={{ color: '#92400e', fontSize: '0.85rem' }}>
                {quoteUnmatchedParts.join(', ')}
              </div>
            </div>
          )}

          <div style={{ marginBottom: '0.5rem' }}>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.35rem' }}>Notes</label>
            <textarea
              style={styles.textarea}
              value={quoteNotes}
              onChange={(e) => setQuoteNotes(e.target.value)}
              rows={2}
            />
          </div>
          {approvalFooterSlots.length > 0 && renderFooterSignatureEditor()}
        </div>

        <div style={styles.modalFooter}>
          <button
            style={{ ...styles.button, ...styles.primaryButton }}
            onClick={onSave}
            disabled={quoteSaving || quoteLineItems.length === 0}
          >
            {quoteSaving ? 'Saving Quote...' : quoteSaveButtonLabel}
          </button>
          <button
            style={{ ...styles.button, ...styles.secondaryButton }}
            onClick={onClose}
            disabled={quoteSaving}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};
